/**
 * Page layers as canvas blocks: how they compile, how they are drawn, how they
 * stay in step with what the feed has stored.
 *
 * A feed's page is divided between LAYERS — each an ordering with its own
 * knobs and thresholds, a share of the page and, optionally, an audience:
 * filters that remove posts, boosts that move them up. This used to be one
 * scrolling form inside the Feed shape; now every layer is a block pinned in
 * the Feed's PAGE section, above the rules, stacked and reordered exactly the
 * way the Logic node's children are.
 *
 * # Layers sit in the Feed, but never in the manifest
 *
 * A layer is bound to the Feed like a rule is, and that is where the likeness
 * ends. `buildTree` compiles the Feed's RULES root into `manifest.filter`, and
 * an operator the matchers do not know is rejected recursively and takes the
 * feed dark. So every reader of the Feed's bindings splits them by TYPE: a
 * Layer child is the Page section, anything else is the Rules root
 * (`isPageRegionChild`). This module compiles the Page section into
 * `custom_sort_settings` — the same wire format the form wrote — and nothing
 * else ever walks it.
 *
 * # Canvas order IS lane order
 *
 * Top to bottom in the Page section is first to last in the stored list, which
 * is the convention the match tree already treats as contract. Lane order is
 * semantic on the serve path: a post two lanes both selected is credited to the
 * EARLIER lane. The form could not reorder layers at all; the canvas makes it
 * a drag, so each block shows its position.
 *
 * # When the list is stored
 *
 * 🔴 Every feed renders as layers — a single-sort feed is one layer at 100% —
 * but the list is only WRITTEN when it says something a plain `sort_order`
 * cannot: two or more layers, an audience, a durable id, a gym slice. A single
 * plain layer's ordering knobs and thresholds are written to the FEED-LEVEL
 * keys they always lived in, so the wire does not change for the 7,358 feeds
 * that open in that state. `needsLensList` is that rule; the stories pin it.
 */

import { deepEqual } from "fast-equals";
import {
  createBindingId,
  createShapeId,
  Editor,
  getIndexAbove,
  IndexKey,
  TLShape,
  TLShapeId,
} from "tldraw";

import { isPageRegionChild } from "@/components/canvas-editor/bindings/layout";
import { ORDERING_SETTING_KEYS } from "@/components/canvas-editor/ordering-settings";
import {
  audienceBoostType,
  AudienceFilterShape,
  audienceFilterType,
  audienceLogicType,
  FeedShape,
  isAudienceBoostShape,
  isAudienceFilterShape,
  isAudienceLogicShape,
  isFeedShape,
  isLayerShape,
  LayerShape,
  layerType,
  LayoutBinding,
} from "@/components/canvas-editor/shapes/shapes";
import { feedId } from "@/components/canvas-editor/utils";
import {
  AUDIENCE_CONTRACT_VERSION,
  AUDIENCE_CONTRACT_VERSION_V3,
  isAudiencePlan,
  isAudiencePlanV3,
  isFilterLeaf,
  isFlatConjunction,
  isPersonalizedOrder,
  filterTreeLeaves,
  lensOrderings,
  newLayerId,
  normaliseAudiencePlan,
  normaliseAudiencePlanV3,
  normaliseLensList,
  readableLens,
  readableLensList,
  rebalanceShares,
  type AudienceFilterNode,
  type AudienceOnEmpty,
  type AudiencePlan,
  type AudiencePlanV3,
  type BlendableLensFacet,
  type FeedLens,
  type LensOrdering,
  type LensSetting,
} from "@/lib/lens";
import { FeedVersion, SortingParams, Thresholds } from "@/lib/types";

/** Keys of a stored lane that are the LANE, not its ordering settings. Mirrors `feed_lenses.LANE_KEYS` plus `lens` and `thresholds`, which the block holds apart. */
const LANE_KEYS = new Set(["share", "sort_order", "id", "gym", "lens", "thresholds"]);

export const LAYER_MIN_W = 540;
export const AUDIENCE_BLOCK_MIN_W = 320;

function pageY(editor: Editor, shape: TLShape | undefined): number {
  if (!shape) return 0;
  return editor.getShapePageBounds(shape)?.y ?? shape.y;
}

function feedShape(editor: Editor): FeedShape | undefined {
  const feed = editor.getShape(feedId);
  return feed && isFeedShape(feed) ? feed : undefined;
}

/** The Feed's Page section: its layer children, first lane first (top to bottom). */
export function getLayerShapes(editor: Editor): LayerShape[] {
  const feed = feedShape(editor);
  if (!feed) return [];
  return editor
    .getBindingsFromShape<LayoutBinding>(feed, "layout")
    .map((b) => editor.getShape(b.toId))
    .filter((s): s is LayerShape => Boolean(s) && isLayerShape(s!))
    .sort((a, b) => pageY(editor, a) - pageY(editor, b));
}

/** A container's pinned blocks, in the order they sit inside it. */
export function layerChildren(editor: Editor, container: TLShape): TLShape[] {
  return editor
    .getBindingsFromShape<LayoutBinding>(container, "layout")
    .map((b) => editor.getShape(b.toId))
    .filter((s): s is TLShape => Boolean(s))
    .sort((a, b) => pageY(editor, a) - pageY(editor, b));
}

/** The layer a block is pinned DIRECTLY inside, if any. */
export function parentLayerOf(editor: Editor, shape: TLShape | undefined): LayerShape | undefined {
  // A block's reactive derivations can run once more while it is being
  // deleted, with nothing behind the reference; answer "no layer" rather
  // than throw.
  if (!shape || !editor.getShape(shape.id)) return undefined;
  const binding = editor.getBindingsToShape<LayoutBinding>(shape, "layout")[0];
  if (!binding) return undefined;
  const parent = editor.getShape(binding.fromId);
  return parent && isLayerShape(parent) ? parent : undefined;
}

/**
 * The layer a block ultimately sits in, through any audience groups between.
 *
 * A group with no layer above it is not applied to anything, and neither is
 * whatever is inside it — so this is what "bound" means for a filter.
 */
export function rootLayerOf(editor: Editor, shape: TLShape | undefined): LayerShape | undefined {
  if (!shape || !editor.getShape(shape.id)) return undefined;
  let current: TLShape | undefined = shape;
  for (let guard = 0; current && guard < 32; guard++) {
    const binding: LayoutBinding | undefined = editor.getBindingsToShape<LayoutBinding>(
      current,
      "layout",
    )[0];
    if (!binding) return undefined;
    const parent: TLShape | undefined = editor.getShape(binding.fromId);
    if (!parent) return undefined;
    if (isLayerShape(parent)) return parent;
    if (!isAudienceLogicShape(parent)) return undefined;
    current = parent;
  }
  return undefined;
}

/** Whether a layer is pinned in the Feed's Page section (and so counts). */
export function layerIsBound(editor: Editor, layer: LayerShape): boolean {
  const binding = editor.getBindingsToShape<LayoutBinding>(layer, "layout")[0];
  if (!binding) return false;
  const parent = editor.getShape(binding.fromId);
  return Boolean(parent && isFeedShape(parent));
}

/**
 * How many groups sit between a container and its layer: a layer is 0, a
 * group directly inside a layer is 1. The contract caps a tree at
 * `MAX_AUDIENCE_FILTER_DEPTH` levels.
 */
export function groupDepthOf(editor: Editor, container: TLShape): number {
  let depth = 0;
  let current: TLShape | undefined = container;
  for (let guard = 0; current && isAudienceLogicShape(current) && guard < 32; guard++) {
    depth++;
    const binding: LayoutBinding | undefined = editor.getBindingsToShape<LayoutBinding>(
      current,
      "layout",
    )[0];
    if (!binding) break;
    current = editor.getShape(binding.fromId);
  }
  return depth;
}

/** Every "Only show" block under a layer or group, through nested groups, in canvas order. */
export function layerFilterLeaves(editor: Editor, container: TLShape): AudienceFilterShape[] {
  const out: AudienceFilterShape[] = [];
  for (const child of layerChildren(editor, container)) {
    if (isAudienceFilterShape(child)) out.push(child);
    else if (isAudienceLogicShape(child)) out.push(...layerFilterLeaves(editor, child));
  }
  return out;
}

/**
 * The facets a layer's blocks read — the fetch budget counts THESE, one blob
 * per page each, not the blocks. Six is the cap in all three parsers.
 */
export function layerFacets(
  editor: Editor,
  layer: LayerShape,
  exceptShapeId?: TLShapeId,
): Set<BlendableLensFacet> {
  const out = new Set<BlendableLensFacet>();
  for (const leaf of layerFilterLeaves(editor, layer)) {
    if (leaf.id !== exceptShapeId) out.add(leaf.props.facet);
  }
  for (const child of layerChildren(editor, layer)) {
    if (child.id !== exceptShapeId && isAudienceBoostShape(child)) out.add(child.props.facet);
  }
  return out;
}

/**
 * The filter tree a container's blocks describe, in canvas order.
 *
 * A layer's direct filters and groups are an implicit `and`; a group is the
 * `and`/`or` its block says. An empty group contributes nothing rather than
 * an empty list the parsers refuse. One node at the top is returned as itself.
 */
function compileFilterTree(editor: Editor, container: TLShape): AudienceFilterNode | null {
  const nodes = compileChildren(editor, container);
  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0]!;
  return { and: nodes };
}

function compileChildren(editor: Editor, container: TLShape): AudienceFilterNode[] {
  const nodes: AudienceFilterNode[] = [];
  for (const child of layerChildren(editor, container)) {
    if (isAudienceFilterShape(child)) {
      nodes.push({
        facet: child.props.facet,
        ...(child.props.min !== undefined ? { min: child.props.min } : {}),
      });
    } else if (isAudienceLogicShape(child)) {
      const inner = compileChildren(editor, child);
      if (inner.length > 0) {
        nodes.push(child.props.type === "and" ? { and: inner } : { or: inner });
      }
    }
  }
  return nodes;
}

/**
 * A layer's audience, compiled from its blocks — or the legacy setting it
 * carries when it has none.
 *
 * `normaliseAudiencePlan` mirrors both backend parsers, so what comes out is
 * what the serve path will honour. `undefined` means no audience.
 */
function compileLayerLens(editor: Editor, layer: LayerShape): LensSetting | undefined {
  const tree = compileFilterTree(editor, layer);
  const blend = layerChildren(editor, layer)
    .filter(isAudienceBoostShape)
    .map((s) => ({ facet: s.props.facet, weight: s.props.weight }));

  if (!tree && blend.length === 0) {
    return readableLens(layer.props.legacyLens) ?? undefined;
  }

  const boost = blend.length
    ? { strength: layer.props.boostStrength, combine: layer.props.boostCombine, blend }
    : null;

  // 🔴 v2 WHENEVER v2 CAN SAY IT. A leaf or an `and` of leaves is the flat
  // list v2 always was, and writing it as v2 keeps every existing feed on the
  // path every pod already reads. Only a tree v2 cannot express — an `or`, or
  // a nested group — is written as v3.
  if (isFlatConjunction(tree)) {
    return (
      normaliseAudiencePlan({
        v: AUDIENCE_CONTRACT_VERSION,
        filters: filterTreeLeaves(tree).map((leaf) => ({
          facet: leaf.facet,
          on_empty: layer.props.onEmpty ?? "unfiltered",
          ...(leaf.min !== undefined ? { min: leaf.min } : {}),
        })),
        boost,
      }) ?? undefined
    );
  }
  return (
    normaliseAudiencePlanV3({
      v: AUDIENCE_CONTRACT_VERSION_V3,
      filter: tree,
      on_empty: layer.props.onEmpty ?? "unfiltered",
      boost,
    }) ?? undefined
  );
}

/** A layer's own ordering settings: only the knob keys, only when numeric. */
function ownSettings(layer: LayerShape): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(layer.props.settings)) {
    if (ORDERING_SETTING_KEYS.has(key) && typeof value === "number" && Number.isFinite(value)) {
      out[key] = value;
    }
  }
  return out;
}

function hasThresholds(thresholds: Thresholds | null | undefined): thresholds is Thresholds {
  if (!thresholds) return false;
  return Boolean(thresholds.AND?.length || thresholds.OR?.length);
}

/** One layer as the lane the serve path reads. */
/**
 * What a gym lane serves while nothing is being tested: the feed's MAIN layer
 * — the non-gym layer with the largest share, top-first on a tie — ordering
 * and parameters alike, so an idle gym slice is indistinguishable from the
 * rest of the page. The gym layer keeps no ordering of its own; the allocator
 * overwrites the lane's ordering when an experiment runs and this is what it
 * falls back to. (True apportioning — the idle slice's share going back to
 * the other layers — is a serve-path change in feed-processor and its
 * feed-gym mirrors, not something the editor can express.)
 */
function idleOrderingFor(
  editor: Editor,
  gym: LayerShape,
): { sort_order: LensOrdering; settings: Record<string, unknown> } {
  const others = getLayerShapes(editor).filter((l) => !l.props.gym && l.id !== gym.id);
  const main = others.reduce<LayerShape | undefined>(
    (best, l) => (!best || l.props.share > best.props.share ? l : best),
    undefined,
  );
  return main
    ? { sort_order: main.props.sort_order, settings: ownSettings(main) }
    : { sort_order: gym.props.sort_order, settings: ownSettings(gym) };
}

export function compileLayer(editor: Editor, layer: LayerShape): FeedLens {
  const lens = compileLayerLens(editor, layer);
  const idle = layer.props.gym ? idleOrderingFor(editor, layer) : undefined;
  return {
    ...(idle ? idle.settings : ownSettings(layer)),
    // A gym layer has no thresholds of its own: what fills it is the arm's
    // (or the main layer's) business, not a second gate on top.
    ...(!layer.props.gym && hasThresholds(layer.props.thresholds)
      ? { thresholds: layer.props.thresholds }
      : {}),
    ...(layer.props.layerId ? { id: layer.props.layerId } : {}),
    ...(layer.props.gym ? { gym: true } : {}),
    share: layer.props.share,
    sort_order: idle ? idle.sort_order : layer.props.sort_order,
    ...(lens ? { lens } : {}),
  };
}

/** Every layer in the Page section as lanes, in lane order. */
export function compileLayers(editor: Editor): FeedLens[] {
  return getLayerShapes(editor).map((layer) => compileLayer(editor, layer));
}

/** A stored lane's own ordering settings: everything that is not the lane itself. */
function laneOwnSettings(lane: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(lane)) {
    if (!LANE_KEYS.has(key)) out[key] = value;
  }
  return out;
}

/**
 * Whether these lanes say anything a bare `sort_order` cannot.
 *
 * See the module header: a single plain layer is the synthesised view of an
 * unlayered feed and must NOT be written as a list; its knobs and thresholds
 * go to the feed-level keys instead.
 */
function needsLensList(lanes: FeedLens[]): boolean {
  if (lanes.length !== 1) return lanes.length > 1;
  const only = lanes[0]!;
  return Boolean(only.id || only.gym || only.lens);
}

/**
 * The feed's sort settings and ordering as the Page section describes them.
 *
 * With no layers (a Personalized feed, or a canvas holding a custom node) the
 * Feed's props are returned as they are.
 */
export function buildSortSettings(
  editor: Editor,
  feed: FeedShape,
): { custom_sort_settings: SortingParams | undefined; order: FeedVersion["order"] } {
  const stored = feed.props.custom_sort_settings;
  const layers = getLayerShapes(editor);
  const lanes = layers.map((l) => compileLayer(editor, l));

  if (lanes.length === 0) {
    return { custom_sort_settings: stored || undefined, order: feed.props.order };
  }

  const next = { ...(stored ?? {}) } as Record<string, unknown>;
  if (needsLensList(lanes)) {
    // Stored only in a shape the serve path could honour; a list it would refuse
    // is a feed that looks configured and serves unlensed.
    const saved = normaliseLensList(lanes) ?? lanes;
    next.lenses = saved;
    // 🔴 `lens` IS KEPT IN SYNC WHILE THERE IS ONE LAYER. feeder-rs builds that
    // predate one-lane lists read the single key; from two layers up each layer
    // owns its own lens and a top-level value could only contradict them.
    const only = saved.length === 1 ? saved[0] : undefined;
    if (only?.lens) next.lens = only.lens;
    else delete next.lens;
  } else {
    // One plain layer: the feed's own ordering, written where it always was.
    delete next.lenses;
    delete next.lens;
    const own = ownSettings(layers[0]!);
    for (const key of ORDERING_SETTING_KEYS) {
      if (key in own) next[key] = own[key];
      else delete next[key];
    }
    if (hasThresholds(layers[0]!.props.thresholds)) next.thresholds = layers[0]!.props.thresholds;
    else delete next.thresholds;
  }

  const empty = Object.keys(next).length === 0;
  return {
    custom_sort_settings: empty && !stored ? undefined : (next as SortingParams),
    // The feed's own ordering follows its FIRST layer, so a one-layer feed keeps
    // `sort_order` meaningful for everything that still reads it.
    order: lanes[0]!.sort_order,
  };
}

function orderingOf(order: string | undefined): LensOrdering {
  return order && (lensOrderings as readonly string[]).includes(order)
    ? (order as LensOrdering)
    : "new";
}

/**
 * The lanes a feed's stored settings describe, synthesising one plain layer
 * (carrying the feed-level knobs and thresholds) when it has none. Empty for
 * orderings that cannot hold a layer at all.
 */
function lanesFromSettings(
  settings: SortingParams | null | undefined,
  order: string | undefined,
): FeedLens[] {
  if (order && isPersonalizedOrder(order)) return [];
  const fallback = orderingOf(order);
  const stored = readableLensList(settings?.lenses, fallback);
  if (stored) return stored;
  const lens = readableLens(settings?.lens);
  const knobs: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(settings ?? {})) {
    if (ORDERING_SETTING_KEYS.has(key) && typeof value === "number") knobs[key] = value;
  }
  return [
    {
      ...knobs,
      ...(hasThresholds(settings?.thresholds) ? { thresholds: settings?.thresholds } : {}),
      share: 1,
      sort_order: fallback,
      ...(lens ? { lens } : {}),
    },
  ];
}

function bind(editor: Editor, fromId: TLShapeId, toId: TLShapeId, index: IndexKey) {
  // One layout binding per child, whatever bound it first. The editor's
  // after-create handler binds any block created over a container at a
  // position-derived index; a layer placed by a tool is created over the
  // Feed and then bound HERE, last — two bindings to one shape gave the stack
  // an empty slot at the top and counted the layer twice.
  for (const existing of editor.getBindingsToShape<LayoutBinding>(toId, "layout")) {
    editor.deleteBinding(existing);
  }
  editor.createBinding<LayoutBinding>({
    id: createBindingId(),
    type: "layout",
    fromId,
    toId,
    props: { index, placeholder: false },
  });
  const child = editor.getShape(toId)!;
  editor.updateShape({ id: toId, type: child.type, parentId: fromId });
}

/** The highest binding index among a container's children, so a new child lands last. */
function lastIndex(editor: Editor, containerId: TLShapeId, onlyPage?: boolean): IndexKey | undefined {
  let index: IndexKey | undefined;
  for (const b of editor.getBindingsFromShape<LayoutBinding>(containerId, "layout")) {
    if (onlyPage) {
      const s = editor.getShape(b.toId);
      if (!s || !isPageRegionChild(s)) continue;
    }
    if (!index || b.props.index > index) index = b.props.index;
  }
  return index;
}

/** The strictest `on_empty` across a v2 plan's filters — what the serve path composed anyway. */
function strictestOnEmpty(plan: AudiencePlan): AudienceOnEmpty {
  return plan.filters.some((f) => f.on_empty === "strict") ? "strict" : "unfiltered";
}

/** The audience blocks a plan describes, drawn into a layer. */
function drawAudiencePlan(
  editor: Editor,
  layerId: TLShapeId,
  plan: AudiencePlan | AudiencePlanV3,
  at: { x: number; y: number },
  startIndex?: IndexKey,
) {
  let index = startIndex;
  const next = () => {
    index = getIndexAbove(index);
    return index;
  };
  if (isAudiencePlan(plan)) {
    for (const filter of plan.filters) {
      const childId = createShapeId();
      editor.createShape({
        id: childId,
        type: audienceFilterType,
        x: at.x,
        y: at.y,
        props: { facet: filter.facet, min: filter.min },
      });
      bind(editor, layerId, childId, next());
    }
  } else if (plan.filter) {
    drawFilterNode(editor, layerId, plan.filter, at, next());
  }
  for (const entry of plan.boost?.blend ?? []) {
    const childId = createShapeId();
    editor.createShape({
      id: childId,
      type: audienceBoostType,
      x: at.x,
      y: at.y,
      props: { facet: entry.facet, weight: entry.weight },
    });
    bind(editor, layerId, childId, next());
  }
}

/** One v3 tree node as blocks: a leaf is a filter block, a group is an audience group with its children pinned inside. */
function drawFilterNode(
  editor: Editor,
  containerId: TLShapeId,
  node: AudienceFilterNode,
  at: { x: number; y: number },
  index: IndexKey,
) {
  if (isFilterLeaf(node)) {
    const childId = createShapeId();
    editor.createShape({
      id: childId,
      type: audienceFilterType,
      x: at.x,
      y: at.y,
      props: { facet: node.facet, min: node.min },
    });
    bind(editor, containerId, childId, index);
    return;
  }
  const groupId = createShapeId();
  editor.createShape({
    id: groupId,
    type: audienceLogicType,
    x: at.x,
    y: at.y,
    props: { type: "and" in node ? "and" : "or" },
  });
  bind(editor, containerId, groupId, index);
  const children = "and" in node ? node.and : node.or;
  let inner: IndexKey | undefined;
  for (const child of children) {
    inner = getIndexAbove(inner);
    drawFilterNode(editor, groupId, child, at, inner);
  }
}

/**
 * Draw one lane as a layer block pinned into the Feed's Page section (last),
 * with its audience blocks pinned inside it.
 */
function drawLayer(editor: Editor, lane: FeedLens, feed: FeedShape): LayerShape {
  const id = createShapeId();
  const lens = lane.lens;
  const plan = isAudiencePlan(lens) || isAudiencePlanV3(lens) ? lens : undefined;
  const own = laneOwnSettings(lane as unknown as Record<string, unknown>);
  const thresholds = (lane as unknown as { thresholds?: Thresholds }).thresholds;

  editor.createShape({
    id,
    type: layerType,
    x: feed.x,
    y: feed.y,
    props: {
      layerId: typeof lane.id === "string" ? lane.id : undefined,
      share: lane.share,
      sort_order: lane.sort_order,
      settings: own,
      thresholds: hasThresholds(thresholds) ? thresholds : undefined,
      gym: Boolean(lane.gym),
      boostStrength: plan?.boost?.strength ?? 1,
      boostCombine: plan?.boost?.combine ?? "max",
      onEmpty: plan
        ? isAudiencePlan(plan)
          ? strictestOnEmpty(plan)
          : plan.on_empty
        : "unfiltered",
      legacyLens: lens && !plan ? lens : undefined,
    },
  });

  bind(editor, feed.id, id, getIndexAbove(lastIndex(editor, feed.id, true)));
  if (plan) drawAudiencePlan(editor, id, plan, { x: feed.x, y: feed.y });

  return editor.getShape<LayerShape>(id)!;
}

/**
 * Draw a feed's layers into its Page section, from its stored settings.
 *
 * Called wherever the Feed shape itself is built from a manifest, so a canvas
 * built from JSON — a remix, a keystroke in the JSON editor, a feed with no
 * snapshot — shows its layers as it shows its rules.
 */
/**
 * Hold the page's share refit while `fn` runs, then refit once.
 *
 * 🔴 The editor refits every layer's share when a layer joins or leaves the
 * Feed (CanvasEditor's operation-complete handler). Two things bind layers
 * TRANSIENTLY: a rebuild draws the stored lanes one at a time, and a drop or
 * an arrow-key nudge unbinds a layer and binds it again — tldraw runs each of
 * those as its own operation, sixteen for one nudge. Refitting at each showed
 * the first lane alone at 100%, or the other layers alone at 100%, and the
 * page came back skewed: [0.5, 0.5] → [0.66, 0.34] from a single nudge (live,
 * 9/16). Held, the page is only refit as it ends up.
 */
let refitHeld = 0;
export function pageRefitHeld(): boolean {
  return refitHeld > 0;
}
export function withPageRefitHeld<T>(editor: Editor, fn: () => T): T {
  refitHeld++;
  let out: T;
  try {
    out = fn();
  } finally {
    refitHeld--;
  }
  if (refitHeld === 0) renormaliseShares(editor);
  return out;
}

export function buildLayerShapes(editor: Editor, feed: FeedShape) {
  withPageRefitHeld(editor, () => {
    for (const lane of lanesFromSettings(feed.props.custom_sort_settings, feed.props.order)) {
      drawLayer(editor, lane, feed);
    }
  });
}

/** Remove every layer and everything pinned inside it, bound or not. */
export function deleteLayerShapes(editor: Editor) {
  const layers = editor.getCurrentPageShapes().filter(isLayerShape);
  const ids = layers.flatMap((layer) => [
    ...layerChildren(editor, layer).flatMap((c) => [
      ...(isAudienceLogicShape(c) ? layerChildren(editor, c).map((g) => g.id) : []),
      c.id,
    ]),
    layer.id,
  ]);
  if (!ids.length) return;
  // Every layer is going: refitting the survivors of each deletion in turn
  // is churn on shapes about to vanish.
  withPageRefitHeld(editor, () => editor.deleteShapes(ids));
}

function comparable(lanes: FeedLens[] | undefined): FeedLens[] | null | undefined {
  return lanes ? normaliseLensList(lanes) : lanes;
}

/**
 * Make the canvas's layers agree with what the feed has stored, after a
 * snapshot load.
 *
 * A snapshot saved before layers were blocks has none, and a feed with no
 * layer block would have no sort control at all — so draw them. A snapshot
 * whose layers compile to something OTHER than the stored list is the known
 * snapshot-vs-manifest drift, one setting over; the stored list is what the
 * serve path reads, so it wins. Done before the change handler is registered,
 * so it dirties nothing.
 */
export function reconcileLayerShapes(editor: Editor, feed: FeedShape, version?: FeedVersion) {
  const settings = version?.custom_sort_settings ?? feed.props.custom_sort_settings;
  const order = version?.order ?? feed.props.order;
  const wanted = lanesFromSettings(settings, order);
  const drawn = getLayerShapes(editor);

  if (drawn.length === 0 && wanted.length === 0) return;
  if (drawn.length > 0 && deepEqual(comparable(compileLayers(editor)), comparable(wanted))) {
    return;
  }

  deleteLayerShapes(editor);
  if (settings !== feed.props.custom_sort_settings || order !== feed.props.order) {
    editor.updateShape<FeedShape>({
      id: feed.id,
      type: feed.type,
      props: { custom_sort_settings: settings ?? undefined, order: orderingOf(order) },
    });
  }
  const current = editor.getShape<FeedShape>(feed.id);
  if (current) buildLayerShapes(editor, current);
}

/**
 * Add a layer to the Page section, last.
 *
 * Its share is the average of the others — exactly 1/n once renormalised.
 * Its ordering is the first one not already in use: `new` is the ordering of
 * 5,957 of 7,358 live feeds, so a fixed default made "add a layer" an instant
 * duplicate on the commonest feed in the fleet.
 *
 * 🔴 A MINTED ID. An id is what survives the creator reordering or removing
 * layers — without one the layer is identified by its position, and a reader
 * resuming after a reorder resumes a different layer.
 */
export function addLayerShape(
  editor: Editor,
  { gym = false, sort_order }: { gym?: boolean; sort_order?: LensOrdering } = {},
): LayerShape | undefined {
  const feed = feedShape(editor);
  if (!feed) return undefined;
  const existing = getLayerShapes(editor);
  if (gym && existing.some((l) => l.props.gym)) return undefined;
  if (existing.length >= 10) return undefined;

  const used = new Set(existing.map((l) => l.props.sort_order));
  const order = sort_order ?? (lensOrderings.find((o) => !used.has(o)) ?? lensOrderings[0]);
  const share = existing.length
    ? existing.reduce((sum, l) => sum + l.props.share, 0) / existing.length
    : 1;

  const layer = drawLayer(
    editor,
    { id: newLayerId(), share, sort_order: order, ...(gym ? { gym: true } : {}) },
    feed,
  );
  renormaliseShares(editor);
  return layer;
}

/** Write shares back so the list sums to 1 — what the serve path does anyway, made visible. */

/**
 * Put a new audience block straight into a layer, last — the Audience row's
 * own "+ Only show / + Move up / + Group" buttons, for the creator who does
 * not want to hunt the palette. Refused (nothing created) when the layer's
 * facet budget or leaf cap would not admit it.
 */
export function addAudienceBlock(
  editor: Editor,
  layer: LayerShape,
  type: typeof audienceFilterType | typeof audienceBoostType | typeof audienceLogicType,
): TLShape | undefined {
  const id = createShapeId();
  editor.createShape({ id, type, x: layer.x, y: layer.y });
  const created = editor.getShape(id);
  if (!created) return undefined;
  const util = editor.getShapeUtil(created) as { canPlaceInShape?: (c: TLShape, s: TLShape) => boolean };
  if (util.canPlaceInShape && !util.canPlaceInShape(layer, created)) {
    editor.deleteShape(id);
    return undefined;
  }
  bind(editor, layer.id, id, getIndexAbove(lastIndex(editor, layer.id)));
  return editor.getShape(id);
}

/**
 * Write every changed share in ONE store operation.
 *
 * 🔴 One `updateShape` per layer completed an operation per layer, and the
 * editor's operation-complete refit re-entered this loop after the FIRST
 * write, refit the half-written page, and then the loop wrote the rest of
 * the stale values over it: a drag-back left [0.25, 0.67] on the page
 * (chaos seed 2, step 41, 9/16). A single `updateShapes` completes once,
 * with every share in place.
 */
function writeShares(editor: Editor, layers: LayerShape[], shares: number[]) {
  const updates = layers.flatMap((l, i) => {
    const next = shares[i];
    return next !== undefined && next !== l.props.share
      ? [{ id: l.id, type: l.type, props: { share: next } }]
      : [];
  });
  if (updates.length) editor.updateShapes<LayerShape>(updates);
}

/**
 * Refit every bound layer's share so the page sums to one. Called by the
 * editor whenever a layer joins or leaves the Feed — added, deleted, dragged
 * out or back in — since three layers at a third each become 75% of a page
 * the moment one of them goes.
 */
export function renormaliseShares(editor: Editor) {
  const layers = getLayerShapes(editor);
  const normalised = normaliseLensList(layers.map((l) => compileLayer(editor, l)));
  if (!normalised) return;
  writeShares(editor, layers, normalised.map((l) => l.share));
}

/**
 * Hold one layer at a share and refit its siblings around it. The gym layer
 * is no exception: a creator may hand the whole page to the gym (Devin, 9/16),
 * and its share is the one dial for how much of the feed is experimental.
 */
export function setLayerShare(editor: Editor, layer: LayerShape, share: number) {
  const layers = getLayerShapes(editor);
  const index = layers.findIndex((l) => l.id === layer.id);
  if (index < 0) return;
  const rebalanced = rebalanceShares(layers.map((l) => compileLayer(editor, l)), index, share);
  writeShares(editor, layers, rebalanced.map((l) => l.share));
}

/**
 * Turn a layer's legacy lens into blocks, on the creator's say-so.
 *
 * A v1 `filter` policy becomes a BOOST, not a filter (`lensToAudience`): v1's
 * filter could never empty a page, and converting it into a v2 filter would hand
 * the feed a power it never had.
 */
export function convertLegacyLens(
  editor: Editor,
  layer: LayerShape,
  plan: AudiencePlan | AudiencePlanV3 | null,
) {
  if (!plan) return;
  editor.run(() => {
    editor.updateShape<LayerShape>({
      id: layer.id,
      type: layer.type,
      props: {
        legacyLens: undefined,
        boostStrength: plan.boost?.strength ?? layer.props.boostStrength,
        boostCombine: plan.boost?.combine ?? layer.props.boostCombine,
        onEmpty: isAudiencePlan(plan) ? strictestOnEmpty(plan) : plan.on_empty,
      },
    });
    drawAudiencePlan(editor, layer.id, plan, { x: layer.x, y: layer.y }, lastIndex(editor, layer.id));
  });
}
